// Lecture d'un VRAI relevé bancaire téléchargé depuis la banque en ligne.
//
// À ne pas confondre avec l'import du tableur de suivi tenu à la main
// (analyserFeuillesMouvements) : ici le fichier vient de la banque, avec la
// disposition que la banque a choisie. Deux formats sont reconnus :
//
//   OFX  format standard proposé par la quasi-totalité des banques françaises
//        (parfois nommé « Money », « Quicken » ou « .ofx »). Sans ambiguïté :
//        les libellés, montants et dates y sont balisés. À PRÉFÉRER.
//   CSV  disposition propre à chaque banque : séparateur, ordre et intitulés
//        des colonnes sont devinés à la lecture de l'en-tête.
//
// Fonctions PURES, comme le reste du module : elles reçoivent du texte et
// rendent des mouvements prêts à insérer. Partagées entre le navigateur et
// d'éventuels scripts serveur, pour que les deux ne divergent jamais sur des
// chiffres d'argent.

import { cellTexte, dateExiste, parseMontantCellule, versDateIso } from './extrabat.js';

export interface MouvementReleve {
  date_mouvement: string; // AAAA-MM-JJ
  libelle: string;
  debit: number; // ce qui sort, toujours positif
  credit: number; // ce qui entre, toujours positif
  pointe: boolean;
  ordre: number; // rang dans une même journée
}

export interface AnalyseReleve {
  mouvements: MouvementReleve[];
  /** Solde annoncé par la banque — sert à vérifier, jamais à écrire. */
  soldeFinalBanque: number | null;
  /**
   * Solde d'ouverture déduit, quand le relevé annonce couvrir l'année depuis
   * le 1er janvier : solde final moins la somme des mouvements. Null sinon —
   * un relevé partiel ne dit rien du solde de début d'année.
   */
  soldeInitialDeduit: number | null;
  /** Compte importé, tel que le nomme la banque. */
  compte: string | null;
  /** Période annoncée par le relevé (AAAA-MM-JJ), quand il l'indique. */
  debutPeriode: string | null;
  finPeriode: string | null;
  /**
   * Autres comptes présents dans le même fichier, NON importés. Les banques
   * réunissent volontiers plusieurs comptes dans un seul export ; les mélanger
   * fausserait la trésorerie de plusieurs dizaines de milliers d'euros.
   */
  autresComptes: string[];
  format: 'ofx' | 'csv' | 'classeur';
  /** Lignes écartées faute de date ou de montant exploitable. */
  ignorees: number;
}

/**
 * Octets d'un fichier -> texte.
 *
 * Les relevés des banques françaises sont souvent encodés en Windows-1252 et
 * non en UTF-8 : lus comme de l'UTF-8, « VIREMENT REÇU » devient illisible.
 * On tente l'UTF-8, et on retombe sur Windows-1252 dès qu'apparaît un
 * caractère de remplacement, signe que le décodage a échoué.
 */
export function decoderTexteBancaire(donnees: ArrayBuffer | Uint8Array): string {
  const octets = donnees instanceof Uint8Array ? donnees : new Uint8Array(donnees);
  const utf8 = new TextDecoder('utf-8').decode(octets);
  if (!utf8.includes('�')) return utf8;
  try {
    return new TextDecoder('windows-1252').decode(octets);
  } catch {
    return utf8;
  }
}

/** Date d'un relevé : accepte aussi « 15/01/26 », que certaines banques écrivent. */
function versDateReleve(brut: string): string | null {
  const s = brut.trim();
  const court = /^(\d{2})\/(\d{2})\/(\d{2})$/.exec(s);
  if (court) {
    const [, jj, mm, aa] = court;
    if (jj && mm && aa) {
      const iso = `20${aa}-${mm}-${jj}`;
      return dateExiste(iso) ? iso : null;
    }
  }
  // « 20260115 » (OFX, et quelques exports CSV)
  const compact = /^(\d{4})(\d{2})(\d{2})/.exec(s);
  if (compact) {
    const [, a, m, j] = compact;
    if (a && m && j) {
      const iso = `${a}-${m}-${j}`;
      return dateExiste(iso) ? iso : null;
    }
  }
  return versDateIso(s);
}

/**
 * Numérote les mouvements dans leur journée et les remet dans l'ordre
 * chronologique : beaucoup de banques exportent du plus récent au plus ancien,
 * alors que le suivi se lit du 1er au 31.
 */
function finaliser(brut: Omit<MouvementReleve, 'ordre'>[]): MouvementReleve[] {
  const premier = brut[0]?.date_mouvement;
  const dernier = brut[brut.length - 1]?.date_mouvement;
  const lignes = premier && dernier && premier > dernier ? [...brut].reverse() : brut;

  const rangs = new Map<string, number>();
  return lignes.map((m) => {
    const n = (rangs.get(m.date_mouvement) ?? 0) + 1;
    rangs.set(m.date_mouvement, n);
    return { ...m, ordre: n };
  });
}

// ----- OFX -------------------------------------------------------------------

/** Valeur d'une balise OFX, en SGML (sans fermeture) comme en XML. */
function baliseOfx(bloc: string, nom: string): string | null {
  const re = new RegExp(`<${nom}>([^<\\r\\n]*)`, 'i');
  const m = re.exec(bloc);
  return m?.[1]?.trim() ?? null;
}

export function analyserOfx(texte: string): AnalyseReleve {
  const brut: Omit<MouvementReleve, 'ordre'>[] = [];
  let ignorees = 0;

  const blocs = texte.split(/<STMTTRN>/i).slice(1);
  for (const bloc of blocs) {
    const corps = bloc.split(/<\/STMTTRN>/i)[0] ?? bloc;
    const dateBrute = baliseOfx(corps, 'DTPOSTED') ?? baliseOfx(corps, 'DTUSER');
    const montant = parseMontantCellule(baliseOfx(corps, 'TRNAMT'));
    if (!dateBrute || montant === null) {
      ignorees += 1;
      continue;
    }
    const date = versDateReleve(dateBrute.slice(0, 8));
    if (!date) {
      ignorees += 1;
      continue;
    }
    // NAME est court et souvent tronqué ; MEMO porte le détail. On garde les
    // deux quand ils diffèrent, pour que le libellé reste reconnaissable.
    const nom = baliseOfx(corps, 'NAME') ?? '';
    const memo = baliseOfx(corps, 'MEMO') ?? '';
    const libelle = [nom, memo === nom ? '' : memo].filter(Boolean).join(' — ').trim();

    brut.push({
      date_mouvement: date,
      libelle: libelle || 'Opération sans libellé',
      debit: montant < 0 ? Math.abs(montant) : 0,
      credit: montant > 0 ? montant : 0,
      // Jamais pointé d'office : « pointé » est un acte de vérification, il ne
      // doit pas se confondre avec « la banque l'a annoncé ».
      pointe: false,
    });
  }

  const soldeBloc = /<LEDGERBAL>([\s\S]*?)<\/LEDGERBAL>/i.exec(texte)?.[1] ?? texte;
  const solde = parseMontantCellule(baliseOfx(soldeBloc, 'BALAMT'));

  return {
    mouvements: finaliser(brut),
    soldeFinalBanque: solde,
    // Un OFX n'annonce pas sa période de façon fiable : pas de déduction.
    soldeInitialDeduit: null,
    compte: null,
    debutPeriode: null,
    finPeriode: null,
    autresComptes: [],
    format: 'ofx',
    ignorees,
  };
}

// ----- Relevés en grille (CSV, ou classeur exporté par la banque) ------------
//
// Même analyse pour les deux : un CSV est découpé en cellules avant d'arriver
// ici. Les banques exportent la même disposition dans les deux formats.

/** Découpe une ligne CSV en respectant les guillemets. */
function decouper(ligne: string, sep: string): string[] {
  const cellules: string[] = [];
  let courante = '';
  let dansGuillemets = false;
  for (let i = 0; i < ligne.length; i += 1) {
    const c = ligne[i];
    if (c === undefined) continue;
    if (c === '"') {
      if (dansGuillemets && ligne[i + 1] === '"') {
        courante += '"';
        i += 1;
      } else dansGuillemets = !dansGuillemets;
    } else if (c === sep && !dansGuillemets) {
      cellules.push(courante);
      courante = '';
    } else courante += c;
  }
  cellules.push(courante);
  return cellules.map((s) => s.trim());
}

/** Intitulé de colonne ramené à sa forme comparable : « Débit » -> « debit ». */
function normaliser(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

interface Colonnes {
  date: number;
  libelle: number;
  debit: number;
  credit: number;
  montant: number;
}

/** Repère les colonnes utiles dans une ligne d'en-tête, ou null si ce n'en est pas une. */
function reperer(entete: string[]): Colonnes | null {
  const noms = entete.map(normaliser);
  const cherche = (test: (n: string) => boolean, exclus: number[] = []): number =>
    noms.findIndex((n, i) => !exclus.includes(i) && n !== '' && test(n));

  // « date de valeur » existe souvent à côté de « date d'opération » : on
  // retient la date d'opération, celle qui fait foi sur le relevé.
  let date = cherche((n) => n.startsWith('date') && /operation|comptab/.test(n));
  if (date === -1) date = cherche((n) => n.startsWith('date'));
  if (date === -1) return null;

  // Aucune colonne de date ne peut servir de libellé ni de montant : « date de
  // valeur », très répandue, contient « valeur » et se ferait prendre pour la
  // colonne des montants. Une date lue comme un nombre donnerait un montant
  // faux SANS erreur visible (« 16/01/2026 » se lit 16), le pire des cas.
  const pasUneDate = (n: string): boolean => !n.startsWith('date');

  const libelle = cherche(
    (n) =>
      pasUneDate(n) && /libelle|label|nature|detail|description|motif|operation|reference/.test(n),
    [date],
  );
  if (libelle === -1) return null;

  const debit = cherche((n) => n.includes('debit'), [date, libelle]);
  const credit = cherche((n) => n.includes('credit'), [date, libelle, debit]);
  const montant = cherche(
    (n) => pasUneDate(n) && /montant|somme|valeur|amount/.test(n),
    [date, libelle, debit, credit],
  );
  if (debit === -1 && credit === -1 && montant === -1) return null;

  return { date, libelle, debit, credit, montant };
}

/** Un compte tel qu'il apparaît dans le fichier : son en-tête et ses opérations. */
interface BlocCompte {
  libelle: string | null;
  mouvements: Omit<MouvementReleve, 'ordre'>[];
  soldeFinal: number | null;
  debutPeriode: string | null;
  finPeriode: string | null;
  ignorees: number;
}

/**
 * Découpe la grille en comptes.
 *
 * Un export bancaire peut enchaîner plusieurs comptes, chacun précédé de son
 * intitulé, de son solde et de sa période, puis de sa propre ligne d'en-tête.
 * Chaque ligne d'en-tête ouvre donc un nouveau compte.
 */
function decouperEnComptes(grille: string[][], cols: Colonnes, entetes: number[]): BlocCompte[] {
  return entetes.map((debut, k) => {
    const fin = entetes[k + 1] ?? grille.length;
    const avant = grille.slice(Math.max(0, debut - 8), debut);

    // Intitulé du compte : la dernière ligne d'identification avant l'en-tête.
    // La phrase « Liste des opérations du compte entre le … » contient elle
    // aussi le mot « compte » et se ferait prendre pour le nom du compte.
    const estIntitule = (t: string): boolean =>
      /compte|livret|cpt\b/i.test(t) && !/liste des|entre le|solde/i.test(t);
    const libelle =
      [...avant]
        .reverse()
        .find((r) => estIntitule(r[0] ?? ''))?.[0]
        ?.trim() ?? null;

    // « Solde au 02/09/2026 » suivi du montant dans une cellule voisine.
    let soldeFinal: number | null = null;
    for (const r of [...avant].reverse()) {
      const i = r.findIndex((c) => /solde\s+au/i.test(c));
      if (i === -1) continue;
      const montant = r.slice(i + 1).find((c) => parseMontantCellule(c) !== null);
      soldeFinal = montant === undefined ? null : parseMontantCellule(montant);
      break;
    }

    // « … entre le 01/01/2026 et le 02/09/2026 »
    let debutPeriode: string | null = null;
    let finPeriode: string | null = null;
    for (const r of avant) {
      const m = /entre le (\d{2}\/\d{2}\/\d{4}) et le (\d{2}\/\d{2}\/\d{4})/i.exec(r.join(' '));
      if (m?.[1] && m[2]) {
        debutPeriode = versDateReleve(m[1]);
        finPeriode = versDateReleve(m[2]);
        break;
      }
    }

    const mouvements: Omit<MouvementReleve, 'ordre'>[] = [];
    let ignorees = 0;
    for (const c of grille.slice(debut + 1, fin)) {
      const date = versDateReleve(c[cols.date] ?? '');
      if (!date) {
        // Lignes vides, intitulés du compte suivant, totaux : sans intérêt.
        if ((c[cols.libelle] ?? '').trim() !== '') ignorees += 1;
        continue;
      }

      let debit = 0;
      let credit = 0;
      if (cols.debit !== -1 || cols.credit !== -1) {
        debit = Math.abs(parseMontantCellule(c[cols.debit] ?? '') ?? 0);
        credit = Math.abs(parseMontantCellule(c[cols.credit] ?? '') ?? 0);
      } else {
        // Colonne unique signée : négatif = sortie, positif = entrée.
        const m = parseMontantCellule(c[cols.montant] ?? '') ?? 0;
        debit = m < 0 ? Math.abs(m) : 0;
        credit = m > 0 ? m : 0;
      }
      if (debit === 0 && credit === 0) {
        ignorees += 1;
        continue;
      }

      mouvements.push({
        date_mouvement: date,
        // Les libellés bancaires contiennent souvent des retours à la ligne
        // (références de mandat, identifiants SEPA) : on les met à plat.
        libelle: (c[cols.libelle] ?? '').replace(/\s+/g, ' ').trim() || 'Opération sans libellé',
        debit,
        credit,
        pointe: false,
      });
    }

    return { libelle, mouvements, soldeFinal, debutPeriode, finPeriode, ignorees };
  });
}

/**
 * Analyse une grille de relevé déjà découpée en cellules.
 *
 * Rend null si la grille ne ressemble pas à un relevé de banque : c'est ce qui
 * permet à l'appelant de se rabattre sur le tableur de suivi tenu à la main.
 */
export function analyserGrilleBancaire(grille: string[][]): AnalyseReleve | null {
  // Les banques font précéder le tableau de quelques lignes d'identification.
  let cols: Colonnes | null = null;
  let premierEntete = -1;
  for (let i = 0; i < Math.min(grille.length, 40); i += 1) {
    const ligne = grille[i];
    if (!ligne) continue;
    cols = reperer(ligne);
    if (cols) {
      premierEntete = i;
      break;
    }
  }
  if (!cols || premierEntete === -1) return null;

  // Toute ligne identique à l'en-tête ouvre un nouveau compte.
  const entetes = grille.reduce<number[]>((acc, ligne, i) => {
    if (i >= premierEntete && reperer(ligne) !== null) acc.push(i);
    return acc;
  }, []);

  const comptes = decouperEnComptes(grille, cols, entetes).filter(
    (c) => c.mouvements.length > 0 || c.libelle !== null,
  );
  if (comptes.length === 0) return null;

  // Le compte courant est celui que suit la trésorerie ; à défaut, le premier.
  const retenu = comptes.find((c) => /courant/i.test(c.libelle ?? '')) ?? comptes[0];
  if (!retenu) return null;

  const mouvements = finaliser(retenu.mouvements);
  const variation = mouvements.reduce((t, m) => t + m.credit - m.debit, 0);
  // Le solde d'ouverture ne se déduit que si le relevé part du 1er janvier :
  // sur une période partielle, ce calcul donnerait un chiffre faux.
  const partDuPremierJanvier = retenu.debutPeriode?.endsWith('-01-01') ?? false;
  const soldeInitialDeduit =
    retenu.soldeFinal !== null && partDuPremierJanvier
      ? Math.round((retenu.soldeFinal - variation) * 100) / 100
      : null;

  return {
    mouvements,
    soldeFinalBanque: retenu.soldeFinal,
    soldeInitialDeduit,
    compte: retenu.libelle,
    debutPeriode: retenu.debutPeriode,
    finPeriode: retenu.finPeriode,
    autresComptes: comptes.filter((c) => c !== retenu).map((c) => c.libelle ?? 'compte sans nom'),
    format: 'csv',
    ignorees: retenu.ignorees,
  };
}

export function analyserCsvBancaire(texte: string): AnalyseReleve {
  const lignes = texte.split(/\r?\n/).filter((l) => l.trim() !== '');

  // Le séparateur et l'en-tête se déduisent l'un de l'autre : on essaie chaque
  // séparateur et on garde celui qui donne un relevé exploitable. Plus sûr que
  // de compter les occurrences, car la virgule sert aussi de décimale.
  for (const sep of [';', '\t', ',', '|']) {
    const analyse = analyserGrilleBancaire(lignes.map((l) => decouper(l, sep)));
    if (analyse) return analyse;
  }
  throw new Error(
    'Relevé non reconnu : aucune ligne d’en-tête avec une colonne de date, ' +
      'de libellé et de montant (ou débit/crédit). Essayez le format OFX, ' +
      'proposé par la plupart des banques et lu sans ambiguïté.',
  );
}

/**
 * Relevé exporté en classeur (.xlsx) par la banque.
 *
 * À ne pas confondre avec le tableur de suivi tenu à la main, qui a la même
 * extension mais rien à voir dans sa disposition : rend null si ce n'est pas
 * un relevé de banque, pour que l'appelant essaie l'autre analyseur.
 */
export function analyserClasseurBancaire(feuilles: unknown[][][]): AnalyseReleve | null {
  for (const rows of feuilles) {
    const analyse = analyserGrilleBancaire(rows.map((r) => r.map((c) => cellTexte(c))));
    if (analyse) return { ...analyse, format: 'classeur' };
  }
  return null;
}

/** Reconnaît le format tout seul et analyse le relevé. */
export function analyserReleveBancaire(texte: string): AnalyseReleve {
  return /<STMTTRN>/i.test(texte) ? analyserOfx(texte) : analyserCsvBancaire(texte);
}
